import { DollarSign, TrendingUp, TrendingDown, Clock, ArrowUpRight, ArrowDownRight, Percent } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface KPIData {
  confirmedRevenue: number
  expectedRevenue: number
  totalHours: number
  realHourlyRate: number
  billablePercentage: number
  // Comparison deltas
  prevConfirmedRevenue: number
  prevExpectedRevenue: number
  prevTotalHours: number
  prevRealHourlyRate: number
  prevBillablePercentage: number
}

interface ReportsKPIStripProps {
  data: KPIData
  isLoading?: boolean
}

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 })

const formatHours = (hours: number) => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

const calculateDelta = (current: number, previous: number): { value: number; positive: boolean } | null => {
  if (previous === 0) return current > 0 ? { value: 100, positive: true } : null
  const delta = ((current - previous) / previous) * 100
  return { value: Math.abs(delta), positive: delta >= 0 }
}

export function ReportsKPIStrip({ data, isLoading }: ReportsKPIStripProps) {
  const kpis = [
    {
      id: 'confirmed',
      label: 'Receita Confirmada',
      value: formatCurrency(data.confirmedRevenue),
      delta: calculateDelta(data.confirmedRevenue, data.prevConfirmedRevenue),
      icon: DollarSign,
      iconBg: 'bg-success/10',
      iconColor: 'text-success',
      valueColor: 'text-success'
    },
    {
      id: 'expected',
      label: 'Receita Prevista',
      value: formatCurrency(data.expectedRevenue),
      delta: calculateDelta(data.expectedRevenue, data.prevExpectedRevenue),
      icon: TrendingUp,
      iconBg: 'bg-warning/10',
      iconColor: 'text-warning',
      valueColor: 'text-warning'
    },
    {
      id: 'hours',
      label: 'Total de Horas',
      value: formatHours(data.totalHours),
      delta: calculateDelta(data.totalHours, data.prevTotalHours),
      icon: Clock,
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
      valueColor: 'text-foreground'
    },
    {
      id: 'rate',
      label: 'Valor/Hora Real',
      value: formatCurrency(data.realHourlyRate),
      delta: calculateDelta(data.realHourlyRate, data.prevRealHourlyRate),
      icon: TrendingUp,
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
      valueColor: 'text-foreground'
    },
    {
      id: 'billable',
      label: '% Faturável',
      value: `${Math.round(data.billablePercentage)}%`,
      delta: calculateDelta(data.billablePercentage, data.prevBillablePercentage),
      icon: Percent,
      iconBg: data.billablePercentage >= 70 ? 'bg-success/10' : 'bg-warning/10',
      iconColor: data.billablePercentage >= 70 ? 'text-success' : 'text-warning',
      valueColor: data.billablePercentage >= 70 ? 'text-success' : 'text-warning'
    }
  ]

  if (isLoading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[1, 2, 3, 4, 5].map(i => (
          <Card key={i} className="shadow-card animate-pulse">
            <CardContent className="p-4">
              <div className="h-20 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
      {kpis.map((kpi, index) => (
        <Card 
          key={kpi.id} 
          className="shadow-card hover:shadow-card-lg transition-all duration-200"
          style={{ animationDelay: `${index * 50}ms` }}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-muted-foreground truncate">{kpi.label}</p>
                <p className={cn("text-xl font-bold mt-1 tracking-tight", kpi.valueColor)}>
                  {kpi.value}
                </p>
                {kpi.delta && (
                  <div className={cn(
                    "flex items-center gap-1 mt-1 text-xs font-medium",
                    kpi.delta.positive ? "text-success" : "text-destructive"
                  )}>
                    {kpi.delta.positive ? (
                      <ArrowUpRight className="h-3 w-3" />
                    ) : (
                      <ArrowDownRight className="h-3 w-3" />
                    )}
                    <span>{kpi.delta.value.toFixed(1)}%</span>
                    <span className="text-muted-foreground font-normal">vs período</span>
                  </div>
                )}
              </div>
              <div className={cn("rounded-lg p-2 shrink-0", kpi.iconBg)}>
                <kpi.icon className={cn("h-4 w-4", kpi.iconColor)} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
